//
// # Properties Panel section components
//
// Each section renders a slice of the entity draft. Sections are composed
// top-to-bottom by PropertiesPanel. A section is only rendered when its
// corresponding field(s) are present on the descriptor (field-presence drives
// section visibility) — that gating happens in PropertiesPanel, so these
// components assume their inputs exist.
//
// The shared shape is `{ value, onChange, disabled }` per field. Phase 1
// renders everything disabled (read-only); later phases enable editing.
//

import React from "react";
import type { schema } from "GenAlphaSchema";
import type { GeneratorParamSpec } from "ServerInterface";
import type { DescriptorPoint } from "../../types";
import { toPinType, toPortDir } from "../../schemaEnums";
import { ParamFields } from "../common/ParamFields";

// -- Entity type (picker for schematic instances; reuses list-available-symbols) --

export function EntityTypeSection(props: {
  // The currently-selected type (the new type when a change is pending, else
  // the instance's current type).
  value: string;
  // Same-class candidate type names (symbol/generator names). Empty while
  // loading or when type-change isn't supported.
  candidates: ReadonlyArray<string>;
  onChange?: (typeName: string) => void;
  disabled: boolean;
}) {
  const { value, candidates, onChange, disabled } = props;
  // If we have no candidates yet, show the current type read-only.
  if (candidates.length === 0) {
    return (
      <div className="properties-section">
        <div className="properties-section-title">Type</div>
        <div className="properties-field">
          <span className="properties-readonly">{value || "—"}</span>
        </div>
      </div>
    );
  }
  // Ensure the current value appears as an option even if not in candidates.
  // De-duplicate by name: the same symbol name can appear in more than one
  // library, and a type picker keyed/valued by name must list each once (also
  // avoids React duplicate-key warnings).
  const options = [
    ...new Set(
      value !== "" && !candidates.includes(value)
        ? [value, ...candidates]
        : candidates
    ),
  ];
  return (
    <div className="properties-section">
      <div className="properties-section-title">Type</div>
      <div className="properties-field">
        <select
          className="properties-select"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
        >
          {options.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// -- Entity name --

export function EntityNameSection(props: {
  value: string;
  onChange?: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="properties-section">
      <div className="properties-section-title">Name</div>
      <div className="properties-field">
        <input
          className="properties-input"
          type="text"
          value={props.value}
          disabled={props.disabled}
          onChange={(e) => props.onChange?.(e.target.value)}
        />
      </div>
    </div>
  );
}

// -- Position (+ optional layer for layout) --

export function PositionSection(props: {
  position: DescriptorPoint;
  onChange?: (position: DescriptorPoint) => void;
  disabled: boolean;
}) {
  const { position, onChange, disabled } = props;
  return (
    <div className="properties-section">
      <div className="properties-section-title">Position</div>
      <div className="properties-row">
        <div className="properties-field">
          <span className="properties-field-label">X</span>
          <span className="properties-field-control">
            <input
              className="properties-input"
              type="number"
              value={position.x}
              disabled={disabled}
              onChange={(e) =>
                onChange?.({ ...position, x: Number(e.target.value) })
              }
            />
          </span>
        </div>
        <div className="properties-field">
          <span className="properties-field-label">Y</span>
          <span className="properties-field-control">
            <input
              className="properties-input"
              type="number"
              value={position.y}
              disabled={disabled}
              onChange={(e) =>
                onChange?.({ ...position, y: Number(e.target.value) })
              }
            />
          </span>
        </div>
      </div>
    </div>
  );
}

export function LayerSection(props: {
  layer: number;
  onChange?: (layer: number) => void;
  disabled: boolean;
}) {
  return (
    <div className="properties-section">
      <div className="properties-section-title">Layer</div>
      <div className="properties-field">
        <input
          className="properties-input"
          type="number"
          value={props.layer}
          disabled={props.disabled}
          onChange={(e) => props.onChange?.(Number(e.target.value))}
        />
      </div>
    </div>
  );
}

// -- Transform: rotation (0/90/180/270) + flip --

const ROTATIONS: ReadonlyArray<number> = [0, 90, 180, 270];

export function TransformSection(props: {
  rotation: number;
  reflected: boolean;
  onRotationChange?: (rotation: number) => void;
  onReflectedChange?: (reflected: boolean) => void;
  disabled: boolean;
}) {
  const { rotation, reflected, disabled } = props;
  return (
    <div className="properties-section">
      <div className="properties-section-title">Orientation</div>
      <div className="properties-field">
        <span className="properties-field-label">Rotation</span>
        <span className="properties-field-control">
          <select
            className="properties-select"
            value={rotation}
            disabled={disabled}
            onChange={(e) => props.onRotationChange?.(Number(e.target.value))}
          >
            {ROTATIONS.map((r) => (
              <option key={r} value={r}>
                {r}°
              </option>
            ))}
          </select>
        </span>
      </div>
      <div className="properties-field">
        <label className="properties-checkbox-field">
          <input
            type="checkbox"
            checked={reflected}
            disabled={disabled}
            onChange={(e) => props.onReflectedChange?.(e.target.checked)}
          />
          Reflected
        </label>
      </div>
    </div>
  );
}

// -- Pin properties: direction (schematic) / pinType (layout) --

const PORT_DIRS: ReadonlyArray<schema.core.PortDir> = [
  "input",
  "output",
  "inout",
];

export function SchPinPropertiesSection(props: {
  direction: schema.core.PortDir;
  onChange?: (direction: schema.core.PortDir) => void;
  disabled: boolean;
}) {
  return (
    <div className="properties-section">
      <div className="properties-section-title">Pin Properties</div>
      <div className="properties-field">
        <span className="properties-field-label">Direction</span>
        <span className="properties-field-control">
          <select
            className="properties-select"
            value={props.direction}
            disabled={props.disabled}
            onChange={(e) => props.onChange?.(toPortDir(e.target.value))}
          >
            {PORT_DIRS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </span>
      </div>
    </div>
  );
}

const PIN_TYPES: ReadonlyArray<schema.core.PinType> = [
  "signal",
  "power",
  "ground",
  "clock",
];

export function LayoutPinPropertiesSection(props: {
  pinType: schema.core.PinType | undefined;
  onChange?: (pinType: schema.core.PinType) => void;
  disabled: boolean;
}) {
  return (
    <div className="properties-section">
      <div className="properties-section-title">Pin Properties</div>
      <div className="properties-field">
        <span className="properties-field-label">Pin Type</span>
        <span className="properties-field-control">
          <select
            className="properties-select"
            value={props.pinType ?? "signal"}
            disabled={props.disabled}
            onChange={(e) => props.onChange?.(toPinType(e.target.value))}
          >
            {PIN_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </span>
      </div>
    </div>
  );
}

// -- Instance properties (generator params, rendered generically) --

export function InstancePropertiesSection(props: {
  paramSpec: GeneratorParamSpec;
  paramValues: Record<string, string>;
  setParamValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  loading: boolean;
  disabled: boolean;
}) {
  return (
    <div className="properties-section">
      <div className="properties-section-title">Instance Properties</div>
      {props.loading ? (
        <div className="properties-readonly">Loading parameters…</div>
      ) : (
        <ParamFields
          paramSpec={props.paramSpec}
          paramValues={props.paramValues}
          setParamValues={props.setParamValues}
          disabled={props.disabled}
        />
      )}
    </div>
  );
}

// -- Outline (layout instance, read-only extents + show-outline toggle) --

export function OutlineSection(props: {
  extents?: { min: DescriptorPoint; max: DescriptorPoint };
  showOutlines: boolean;
  onShowOutlinesChange?: (show: boolean) => void;
  disabled: boolean;
}) {
  const { extents } = props;
  const width = extents ? extents.max.x - extents.min.x : undefined;
  const height = extents ? extents.max.y - extents.min.y : undefined;
  return (
    <div className="properties-section">
      <div className="properties-section-title">Outline</div>
      {extents ? (
        <>
          <div className="properties-field">
            <span className="properties-field-label">Min</span>
            <span className="properties-readonly">
              ({extents.min.x}, {extents.min.y})
            </span>
          </div>
          <div className="properties-field">
            <span className="properties-field-label">Max</span>
            <span className="properties-readonly">
              ({extents.max.x}, {extents.max.y})
            </span>
          </div>
          <div className="properties-field">
            <span className="properties-field-label">Size</span>
            <span className="properties-readonly">
              {width} × {height}
            </span>
          </div>
        </>
      ) : (
        <div className="properties-field">
          <span className="properties-readonly">No outline set</span>
        </div>
      )}
      <div className="properties-field">
        <label className="properties-checkbox-field">
          <input
            type="checkbox"
            checked={props.showOutlines}
            disabled={props.disabled}
            onChange={(e) => props.onShowOutlinesChange?.(e.target.checked)}
          />
          Show outlines
        </label>
      </div>
    </div>
  );
}
